import React, { useRef, useState } from 'react';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { Textarea } from 'components/UI/V3/Forms/Textarea/Textarea';
import { useEmailMarketing } from '../../EmailMarketingProvider';
import { CampaignDraft, EmailTemplate, TemplateSource } from '../../types';
import { Panel } from '../../UI/Panel/Panel';
import { EmojiPickerButton } from '../../UI/EmojiPickerButton/EmojiPickerButton';
import { insertAtCursor } from '../../utils';
import { SegmentedControl } from '../../UI/SegmentedControl/SegmentedControl';
import TemplateCard from '../../Templates/TemplateCard';
import TemplatePreviewModal from '../../Templates/TemplatePreviewModal';
import StepHeading from './StepHeading';
import styles from './steps.module.scss';

// Same limits as the marketplace wizard (subject 255, preview text 90).
const SUBJECT_MAX_LENGTH = 255;
const PREVIEW_MAX_LENGTH = 90;

// The backend personalizes these in the subject only (EmailCampaign#subject_for); preview text is sent as is.
const MERGE_TAGS = ['{{first_name}}', '{{last_name}}', '{{pet_name}}', '{{business_name}}'];

interface ContentStepProps {
    draft: CampaignDraft;
    onChange: (patch: Partial<CampaignDraft>) => void;
}

export const ContentStep: React.FC<ContentStepProps> = ({ draft, onChange }) => {
    const { templates, locationName } = useEmailMarketing();
    const selectedSource = templates.find(template => template.id === draft.templateId)?.source ?? 'location';
    const [source, setSource] = useState<TemplateSource>(selectedSource);
    const [previewing, setPreviewing] = useState<EmailTemplate | null>(null);
    const subjectRef = useRef<HTMLInputElement>(null);
    const previewRef = useRef<HTMLTextAreaElement>(null);

    const selectTemplate = (template: EmailTemplate) =>
        onChange({ templateId: template.id, subject: draft.subject || template.subjectDefault });

    return (
        <>
            <StepHeading
                title="Design your email"
                description="Pick a template and write the subject customers will see."
            />

            <Panel title="Template">
                <SegmentedControl
                    size="small"
                    options={[
                        { id: 'location' as TemplateSource, label: 'Location templates' },
                        { id: 'gallery' as TemplateSource, label: 'Cuddles gallery' }
                    ]}
                    value={source}
                    onChange={setSource}
                />
                <div className={styles.templateGrid}>
                    {templates
                        .filter(
                            template =>
                                template.source === source &&
                                // Only templates owned by one of the selected locations (or the gallery).
                                (template.source === 'gallery' ||
                                    // API templates don't say which location owns them yet.
                                    template.locationId === null ||
                                    draft.locationIds.includes(template.locationId as number))
                        )
                        .map(template => (
                            <TemplateCard
                                key={template.id}
                                compact
                                template={template}
                                locationName={locationName(template.locationId)}
                                selected={template.id === draft.templateId}
                                onUse={() => selectTemplate(template)}
                                onPreview={() => setPreviewing(template)}
                            />
                        ))}
                </div>
            </Panel>

            <Panel title="Inbox details">
                <div className={styles.fields}>
                    <div>
                        <div className={styles.inputWithEmoji}>
                            <Input
                                ref={subjectRef}
                                label="Subject"
                                placeholder="e.g. Summer spa week is here"
                                value={draft.subject}
                                maxLength={SUBJECT_MAX_LENGTH}
                                autoFocus={false}
                                onChange={event => onChange({ subject: event.target.value })}
                            />
                            <EmojiPickerButton
                                className={styles.subjectEmoji}
                                ariaLabel="Insert emoji into subject"
                                onSelect={emoji => insertAtCursor(subjectRef.current, emoji)}
                            />
                        </div>
                        <div className={styles.mergeTags}>
                            Personalize:
                            {MERGE_TAGS.map(tag => (
                                <button
                                    key={tag}
                                    type="button"
                                    onClick={() => insertAtCursor(subjectRef.current, tag)}
                                >
                                    {tag}
                                </button>
                            ))}
                        </div>
                        <p className={styles.fieldHint}>
                            {'{{business_name}}'} shows each customer’s location. {'{{pet_name}}'} is left blank for
                            customers without pets.
                        </p>
                    </div>

                    <Textarea
                        ref={previewRef}
                        label="Preview text"
                        placeholder="The short line shown after the subject in most inboxes"
                        value={draft.previewText}
                        maxLength={PREVIEW_MAX_LENGTH}
                        footerActions={
                            <EmojiPickerButton
                                className={styles.textareaEmoji}
                                ariaLabel="Insert emoji into preview text"
                                align="start"
                                onSelect={emoji => insertAtCursor(previewRef.current, emoji)}
                            />
                        }
                        onChange={event => onChange({ previewText: event.target.value })}
                    />
                </div>
            </Panel>

            {previewing && (
                <TemplatePreviewModal
                    template={previewing}
                    locationName={locationName(previewing.locationId)}
                    useLabel="Use this template"
                    onClose={() => setPreviewing(null)}
                    onUse={() => {
                        selectTemplate(previewing);
                        setPreviewing(null);
                    }}
                />
            )}
        </>
    );
};

export default ContentStep;
